import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Divider,
  Checkbox,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { QUICK_ACTIONS, type DashboardDialogId, type QuickActionDef } from './quickActions';

interface QuickActionsWidgetProps {
  /** Open a dialog in place on the dashboard. */
  onOpenDialog: (dialog: DashboardDialogId) => void;
  /** Ids of quick actions the user has enabled (shown in normal mode). */
  enabledIds: string[];
  /** In edit mode the widget shows every action with an enable/disable toggle. */
  editMode?: boolean;
  /** Toggle whether an action is enabled (edit mode only). */
  onToggleAction?: (id: string) => void;
}

export function QuickActionsWidget({
  onOpenDialog,
  enabledIds,
  editMode = false,
  onToggleAction,
}: QuickActionsWidgetProps) {
  const { t } = useTranslation('dashboard');
  const theme = useTheme();
  const navigate = useNavigate();

  const performAction = (action: QuickActionDef) => {
    if (action.kind === 'dialog' && action.dialog) {
      onOpenDialog(action.dialog);
    } else if (action.kind === 'navigate' && action.href) {
      void navigate(action.href);
    }
  };

  // Edit mode shows the whole catalog (for toggling); normal mode shows only
  // the enabled actions, in catalog order.
  const shownActions = editMode
    ? QUICK_ACTIONS
    : QUICK_ACTIONS.filter((a) => enabledIds.includes(a.id));

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h6" gutterBottom>
          {t('quickActions.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {editMode ? t('quickActions.editHint') : t('quickActions.description')}
        </Typography>

        <Divider sx={{ mb: 2 }} />

        {/* Fixed-width tiles that wrap: each is ~120px wide, so the row fills
            with as many as fit and the rest wrap to the next line — keeping the
            tiles compact instead of stretching a handful across the full width.
            On phones they grow to share the row so there are no awkward gaps. */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.5,
          }}
        >
          {shownActions.map((action) => {
            const color = action.color(theme);
            const enabled = enabledIds.includes(action.id);
            const dimmed = editMode && !enabled;
            return (
              <Box
                key={action.id}
                sx={{
                  position: 'relative',
                  // Compact fixed-width tile (~120px). On xs it grows to share
                  // the row (no gaps); from sm up it stays 120px so wide screens
                  // pack ~6 per row instead of stretching a few very wide.
                  flex: { xs: '1 1 104px', sm: '0 0 120px' },
                }}
              >
                {editMode && (
                  <Checkbox
                    checked={enabled}
                    onChange={() => onToggleAction?.(action.id)}
                    size="small"
                    aria-label={t(action.labelKey)}
                    sx={{
                      position: 'absolute',
                      top: 2,
                      right: 2,
                      zIndex: 1,
                      p: 0.5,
                    }}
                  />
                )}
                <Button
                  variant="outlined"
                  onClick={() =>
                    editMode ? onToggleAction?.(action.id) : performAction(action)
                  }
                  sx={{
                    width: '100%',
                    height: 80,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    borderColor: 'divider',
                    color: 'text.primary',
                    opacity: dimmed ? 0.45 : 1,
                    '&:hover': {
                      borderColor: color,
                      bgcolor: alpha(color, 0.03),
                      '& .action-icon': {
                        color,
                      },
                    },
                  }}
                >
                  <Box
                    className="action-icon"
                    sx={{
                      color,
                      transition: 'color 0.2s',
                    }}
                  >
                    <action.Icon />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 500,
                      textTransform: 'none',
                      lineHeight: 1.2,
                    }}
                  >
                    {t(action.labelKey)}
                  </Typography>
                </Button>
              </Box>
            );
          })}
        </Box>
      </CardContent>
    </Card>
  );
}
